import { useTranslation } from 'react-i18next'
import styled, { css, keyframes } from 'styled-components'

import { Flex, Icon, Text, Tooltip } from '@percent/lemonade'
import { SimulatedTimeLog } from '@percent/workplace-giving/api/gamification/simulateAccountRewards/simulateAccountRewards.types'
import { useMoneyFormat } from '@percent/workplace-giving/common/hooks'
import { useSimulatedVolunteeringReward } from '@percent/workplace-giving/common/hooks/useSimulatedVolunteeringReward/useSimulatedVolunteeringReward'
import { formatMinutesToHours } from '@percent/workplace-giving/utils/dayjs/dayjs'

type SimulatedVolunteeringRewardProps = Readonly<{
  timeLogs: readonly SimulatedTimeLog[]
  variant?: 'inline' | 'highlighted'
}>

const shakePresent = keyframes`
  0%, 40%, 100% {
    transform: translateY(0) rotate(0);
  }
  8% {
    transform: translateY(-3px) rotate(-12deg);
  }
  16% {
    transform: translateY(-3px) rotate(10deg);
  }
  24% {
    transform: translateY(-1px) rotate(-6deg);
  }
  32% {
    transform: translateY(0) rotate(3deg);
  }
`

const Highlighted = styled.div`
  ${({ theme }) => `
    width: 100%;
    box-sizing: border-box;
    padding: ${theme.spacing.s};
    border: 1px solid rgba(var(--accent100));
    border-radius: ${theme.radii.container};
    background: ${theme.semantic.backgroundAi};
  `}
`

const GiftIcon = styled.span<{ $highlighted: boolean }>`
  display: flex;
  ${({ theme, $highlighted }) =>
    $highlighted
      ? css`
          color: rgba(var(--accent400));
          transform-origin: bottom center;
          animation: ${shakePresent} 2.5s ease-in-out infinite;

          @media (prefers-reduced-motion: reduce) {
            animation: none;
          }
        `
      : css`
          color: ${theme.colors.gray400};
        `}
`

function Gift({ highlighted = false }: Readonly<{ highlighted?: boolean }>) {
  return (
    <GiftIcon $highlighted={highlighted}>
      <Icon name="gift" size={5} color="inherit" />
    </GiftIcon>
  )
}

export function SimulatedVolunteeringReward({ timeLogs, variant = 'inline' }: SimulatedVolunteeringRewardProps) {
  const { t } = useTranslation()
  const { moneyFormat } = useMoneyFormat()
  const { rule, reward } = useSimulatedVolunteeringReward(timeLogs)

  if (!rule || !reward) {
    return null
  }

  const rate = moneyFormat(rule.rewardConfig.alternativeCurrencyAmounts[reward.currency] ?? rule.rewardConfig.amount, {
    hideDecimalsIfZero: true
  })
  const explanation = rule.description
    ? rule.description.replaceAll('{{amount}}', rate)
    : t('workplace_giving.volunteering.rewardBudget.rate', {
        amount: rate,
        duration: formatMinutesToHours(rule.rewardConfig.perMinutes, t)
      })
  const label = t('workplace_giving.volunteering.simulatedReward', {
    amount: moneyFormat(reward, { hideDecimalsIfZero: true })
  })

  return (
    <Tooltip content={explanation} placement="top">
      {variant === 'highlighted' ? (
        <Highlighted data-testid="simulated-volunteering-reward">
          <Flex align="center" gap="xs">
            <Gift highlighted />
            <Text size="small" fontWeight="medium">
              {label}
            </Text>
          </Flex>
        </Highlighted>
      ) : (
        <div data-testid="simulated-volunteering-reward">
          <Gift />
          <Text as="p">{label}</Text>
        </div>
      )}
    </Tooltip>
  )
}
